import { Fragment, FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import { dayjs } from 'src/lib/dayjs'
import { SpacingType } from 'src/tailwind'

import { IconName } from '../icons'
export const InlineIconText: FunctionalComponent<
  {
    icon: IconName
    flip?: boolean
    iconClass?: string
    spacingX?: SpacingType
  } & JSXBase.IntrinsicElements['span']
> = ({ icon, flip, iconClass = '', spacingX = '1', class: className, ...attrs }, children) => {
  // This does not use a `flex items-center` because it needs to work _inline
  // with text_!  Aligning the main span to the bottom and each item to the
  // middle works to allow icons larger than the text be centered by what looks
  // "correct" when used inline with other text.
  // Please do not change this without making sure it works inline with other text.
  return (
    <span class={`${className} select-none align-bottom whitespace-no-wrap spacing-x-${spacingX}`} {...attrs}>
      {flip ? (
        <Fragment>
          <span class='align-middle'>{children}</span>
          <ui-icon icon={icon} class={`align-middle ${iconClass}`} />
        </Fragment>
      ) : (
        <Fragment>
          <ui-icon icon={icon} class={`align-middle ${iconClass}`} />
          <span class='align-middle'>{children}</span>
        </Fragment>
      )}
    </span>
  )
}

export const SOC2Footer: FunctionalComponent<JSXBase.IntrinsicElements['div']> = ({ class: className, ...attrs }) => (
  <div
    {...attrs}
    class={`${className} flex flex-row mx-auto items-center text-base leading-tight text-glyph-lighter py-10 gap-2 px-4 sm:px-0`}
  >
    <ui-icon icon='shield-lock' size='3xl' class='text-glyph-light'></ui-icon>
    <div class='max-w-3xl'>
      Fathom takes security & privacy seriously and is <span class='font-semibold text-glyph-light'>SOC2 Audited</span>.
      Learn more at{' '}
      <a
        href='https://trust.fathom.video'
        target='_blank'
        class='font-semibold text-glyph-light hover:text-glyph-base underline'
      >
        trust.fathom.video
      </a>
    </div>
  </div>
)

export const MiniSOC2Footer: FunctionalComponent<JSXBase.IntrinsicElements['div']> = ({
  class: className,
  ...attrs
}) => (
  <div {...attrs} class={`${className} flex flex-row mx-auto items-center text-sm leading-tight text-glyph-lighter`}>
    <ui-icon icon='shield-lock' size='3xl' class='mt-1 text-glyph-light'></ui-icon>
    <div class='ml-3'>
      Fathom takes security & privacy seriously and is <span class='font-bold text-glyph-light'>SOC2 Audited</span>.
      Learn more at{' '}
      <a href='https://trust.fathom.video' target='_blank' class='font-bold text-glyph-light underline'>
        trust.fathom.video
      </a>
    </div>
  </div>
)

export const TermsFooter: FunctionalComponent<JSXBase.IntrinsicElements['div']> = ({ class: className, ...attrs }) => (
  <div {...attrs} class={`text-center px-4 sm:px-0 text-surface-highest ${className}`}>
    By using Fathom, you agree to the{' '}
    <span>
      <a href='/terms' target='_blank' class='hover:text-glyph-light underline'>
        Terms of Service
      </a>{' '}
      and{' '}
      <a href='/privacy' target='_blank' class='hover:text-glyph-light underline'>
        Privacy Policy
      </a>
    </span>
  </div>
)

export const FullDreamtenFooter: FunctionalComponent<JSXBase.IntrinsicElements['div']> = ({
  class: className,
  ...attrs
}) => (
  <div
    {...attrs}
    class={`${className} flex flex-row w-full text-base leading-tight gap-33px text-default font-semibold pt-6 border-t-1 border-gray-700`}
  >
    <a href='/terms' target='_blank' class='text-white hover:text-brand'>
      Terms of Service
    </a>
    <a href='/privacy' target='_blank' class='text-white hover:text-brand'>
      Privacy Policy
    </a>
    <a href='https://trust.fathom.video' target='_blank' class='text-white hover:text-brand'>
      Security
    </a>
    <a href='https://status.fathom.video' target='_blank' class='text-white hover:text-brand'>
      Status
    </a>
    <a href='https://help.fathom.video' target='_blank' class='text-white hover:text-brand'>
      Help
    </a>
    <div class='ml-auto text-gray-70'>&copy; Fathom {dayjs().year()}. All&nbsp;Rights&nbsp;Reserved.</div>
  </div>
)
